import React from 'react';
import _ from 'underscore';
import { isNullOrUndefined } from 'util';

import { Filter } from '~commonInfra/utils/Filter';
import { BackgroundJobDefinition } from '~src/backgroundJob/components/contentDefinitions/BackgroundJobDefinition';
import { SpaceContentsRequestAction } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { ExplorerTestIds } from '~src/spacesPlatform/components/Explorer/Explorer';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ExplorerEmptyStateProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { ExplorableItemType } from '../../spacesPlatform/models/ContentType';
import { IStringValuedFilterClause } from '../../spacesPlatform/models/IFilterClause';
import { IBackgroundJobStatistics } from '../serverApi/types/IBackgroundJobStatistics';
import { GetBackgroundJobStatisticsRequest, IGetBackgroundJobStatisticsResult } from '../types/GetBackgroundJobStatisticsRequest';
import { BackgroundJobStyles } from './BackgroundJobStyles';


export interface BackgroundJobsSpaceStateProps {
  readonly urls: SpacesSiteUrls;
  readonly statusFilter?: IStringValuedFilterClause;
}

export interface BackgroundJobsSpaceDispatchProps {
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly requestSpaceContents: () => SpaceContentsRequestAction;
}

export interface BackgroundJobsSpaceOwnProps {
  readonly siteName: string;
}

export type BackgroundJobsSpaceProps = BackgroundJobsSpaceStateProps & BackgroundJobsSpaceDispatchProps & BackgroundJobsSpaceOwnProps;

interface BackgroundJobsState {
  statistics?: IBackgroundJobStatistics;
}

export class BackgroundJobsSpace extends React.Component<BackgroundJobsSpaceProps, BackgroundJobsState> {
  constructor(props: BackgroundJobsSpaceProps) {
    super(props);
    this.state = {};
  }

  public render() {
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={BackgroundJobStyles.MastheadArea}>
          <div className={ExplorerStyles.SpaceHeaderContent}>
            <div className={ExplorerStyles.SpaceHeaderText}>{Messages.jobsBrowserTitle()}</div>
          </div>
          <div>
            <div className={BackgroundJobStyles.SummaryInfoButtonGroup}>
              {this.state.statistics && <>
                <span style={BackgroundJobStyles.SummaryInfoItem}>
                  {Messages.backgroundJobStatisticsFailed({ COUNT_LOC: L10n.localizedCount(this.state.statistics.failedBackgroundJobs) })}
                </span>
                <span style={BackgroundJobStyles.SummaryInfoItem}>
                  {Messages.backgroundJobStatisticsCompleted({ COUNT_LOC: L10n.localizedCount(this.state.statistics.succeededBackgroundJobs) })}
                </span>
                <span style={BackgroundJobStyles.SummaryInfoItem}>
                  {Messages.backgroundJobStatisticsCancelled({ COUNT_LOC: L10n.localizedCount(this.state.statistics.cancelledBackgroundJobs) })}
                </span>
              </>
              }
            </div>
          </div>
        </div>
        <div className={SpaceContainerStyles.SubplaceArea}>
          <ConnectedExplorer
            supportedDisplayModes={BackgroundJobDefinition.supportedDisplayModes}
            emptyStateProps={this.emptyStateProps()}
            toolbarProps={{
              supportFilterPanel: true,
              toolbarVisibility: 'enabled',
              hideSelection: true,
            }}
          />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const siteId = BackgroundJobDefinition.defaultFilters('').siteId;
    const filter = isNullOrUndefined(siteId) ? undefined : Filter.and([siteId]);
    WebHttpClientWithErrorToastInstance.sendRequest(new GetBackgroundJobStatisticsRequest({filter}))
      .then((result: IGetBackgroundJobStatisticsResult) => this.setState({
        statistics: result
      }));
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: BackgroundJobsSpaceProps) {
    if(!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.props.requestSpaceContents();
    }
  }

  private emptyStateProps(): ExplorerEmptyStateProps {
    return {
      dataTestId: ExplorerTestIds.ExplorerNoContent,
      iconUrl: '',
      primaryMessage: Messages.contentEmptyMessage(),
      secondaryMessage: '',
    };
  }

  private onNoContentAction = (): void => {
    // Currently there are no actions that can be performed on background jobs
    // See TFS 872202 for the first expected action to be added
  };
}
